import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import Button from '../../../components/ui/Button';
import Card, { CardContent } from '../../../components/ui/Card';
import { getAccountStatus } from '../../../services/stripeConnectService';

const StripeConnectComplete: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'incomplete' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const accountStatus = await getAccountStatus();
        
        if (accountStatus.payoutAccount?.chargesEnabled && accountStatus.payoutAccount?.onboardingComplete) {
          setStatus('success');
          setMessage('Your Stripe account has been successfully connected! You can now receive payments from your patrons.');
        } else if (accountStatus.payoutAccount?.stripeConnectAccountId) {
          setStatus('incomplete');
          setMessage('Your Stripe account is connected, but onboarding is not complete. Please return to the payouts page to complete the setup.');
        } else {
          setStatus('error');
          setMessage('Something went wrong. Please try connecting your Stripe account again.');
        }
      } catch (error: any) {
        console.error('Failed to check Stripe Connect status:', error);
        setStatus('error');
        setMessage(error.response?.data?.message || 'Failed to verify your Stripe account status. Please check the payouts page.');
      }
    };

    checkStatus();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Verifying your account...
              </h2>
              <p className="text-gray-600">
                Please wait while we confirm your Stripe Connect setup.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircleIcon className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Successfully Connected!
              </h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <Button onClick={() => navigate('/dashboard/payouts')} className="w-full">
                Go to Payouts
              </Button>
            </>
          )}

          {status === 'incomplete' && (
            <>
              <ExclamationCircleIcon className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Setup Incomplete
              </h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <Button onClick={() => navigate('/dashboard/payouts')} className="w-full">
                Complete Setup
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <ExclamationCircleIcon className="w-16 h-16 text-red-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Connection Failed
              </h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <Button onClick={() => navigate('/dashboard/payouts')} className="w-full">
                Back to Payouts
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StripeConnectComplete;
